import { api } from './client';
import type { Tip, ChecklistSection, ChecklistItem, ContentSource, PackingScope, PackingCopyResult } from '../types';

export const tipsApi = {
  list: (tripId: string) => api.get<Tip[]>(`/trips/${tripId}/tips`).then(r => r.data),
  create: (tripId: string, data: Partial<Tip>) =>
    api.post<Tip>(`/trips/${tripId}/tips`, data).then(r => r.data),
  update: (tripId: string, tipId: string, data: Partial<Tip>) =>
    api.patch<Tip>(`/trips/${tripId}/tips/${tipId}`, data).then(r => r.data),
  delete: (tripId: string, tipId: string) => api.delete(`/trips/${tripId}/tips/${tipId}`),

  listChecklists: (tripId: string) =>
    api.get<ChecklistSection[]>(`/trips/${tripId}/tips/checklists`).then(r => r.data),
  createSection: (tripId: string, data: { title: string; source?: ContentSource; scope?: PackingScope }) =>
    api.post<ChecklistSection>(`/trips/${tripId}/tips/checklists`, data).then(r => r.data),
  updateSection: (tripId: string, sectionId: string, data: Partial<ChecklistSection>) =>
    api.patch<ChecklistSection>(`/trips/${tripId}/tips/checklists/${sectionId}`, data).then(r => r.data),
  deleteSection: (tripId: string, sectionId: string) =>
    api.delete(`/trips/${tripId}/tips/checklists/${sectionId}`),
  reorderSections: (tripId: string, orderedIds: string[]) =>
    api.patch(`/trips/${tripId}/tips/checklists/reorder`, { orderedIds }),
  seedDefaultSections: (tripId: string, titles: string[]) =>
    api.post<{ seeded: boolean; sections: ChecklistSection[] }>(`/trips/${tripId}/tips/checklists/seed-defaults`, { titles }).then(r => r.data),

  /**
   * Reuses a past trip's list. `scope` is the tab the user is on, exactly like
   * createSection; the server decides what of the source trip is theirs to copy
   * and merges into same-titled sections rather than duplicating them.
   */
  copyFromTrip: (tripId: string, data: { sourceTripId: string; sectionIds: string[]; scope?: PackingScope }) =>
    api.post<PackingCopyResult>(`/trips/${tripId}/tips/checklists/copy`, data).then(r => r.data),

  createItem: (tripId: string, sectionId: string, data: { text: string; assignedToUserId?: string | null }) =>
    api.post<ChecklistItem>(`/trips/${tripId}/tips/checklists/${sectionId}/items`, data).then(r => r.data),
  updateItem: (tripId: string, sectionId: string, itemId: string, data: Partial<ChecklistItem>) =>
    api.patch<ChecklistItem>(`/trips/${tripId}/tips/checklists/${sectionId}/items/${itemId}`, data).then(r => r.data),
  deleteItem: (tripId: string, sectionId: string, itemId: string) =>
    api.delete(`/trips/${tripId}/tips/checklists/${sectionId}/items/${itemId}`),

  // Separate from listChecklists so that response stays a plain array — mobile
  // builds already in the wild consume it as one.
  getPackingSettings: (tripId: string) =>
    api.get<{ sharesPacking: boolean }>(`/trips/${tripId}/tips/checklists/settings`).then(r => r.data),
  updatePackingSettings: (tripId: string, sharesPacking: boolean) =>
    api.patch<{ sharesPacking: boolean }>(`/trips/${tripId}/tips/checklists/settings`, { sharesPacking }).then(r => r.data),
};
